import { buildCompetitionSquadJoinUrl } from '../../competitions/helpers/competitionInviteLink.helper'
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants'
import { PRODUCTION_APP_ORIGIN } from '../../../constants/env'
import { formatDateShort } from '../../../helpers/formatDateTime.helper'
import type { B2bMemberRow } from '../types/b2bAccount.types'

/** Display name + optional @username line for member tables. */
export interface B2bMemberNameCells {
  primary: string
  secondary: string | null
}

/** Auth/db usernames like `user_ee61efc8` are not human-facing; prefer email or "Member". */
const TECHNICAL_MEMBER_USERNAME_RE = /^user_[a-z0-9]+$/i

/** Some environments store the auth user id in `username`; never show that as a display name. */
const USERNAME_LOOKS_LIKE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isNonHumanB2bMemberUsername(username: string, userId: string): boolean {
  const u = username.trim()
  if (!u) return true
  if (TECHNICAL_MEMBER_USERNAME_RE.test(u)) return true
  if (USERNAME_LOOKS_LIKE_UUID_RE.test(u)) return true
  if (userId && u === userId) return true
  return false
}

function displayableMemberEmail(email: string | null | undefined): string {
  if (!email || typeof email !== 'string') return ''
  const t = email.trim()
  if (!t) return ''
  if (t.toLowerCase().includes('@placeholder')) return ''
  return t
}

function humanReadableUsernameForSecondary(
  username: string | null | undefined,
  userId: string,
): string | null {
  if (!username || typeof username !== 'string') return null
  const u = username.trim()
  if (!u || isNonHumanB2bMemberUsername(u, userId)) return null
  return `@${u}`
}

export function getB2bMemberNameCells(m: B2bMemberRow): B2bMemberNameCells {
  const nameParts = [m.firstName, m.lastName].filter(Boolean).join(' ').trim()
  if (nameParts) {
    return {
      primary: nameParts,
      secondary: humanReadableUsernameForSecondary(m.username, m.userId),
    }
  }
  const email = displayableMemberEmail(m.email)
  if (email) {
    return { primary: email, secondary: null }
  }
  const u = typeof m.username === 'string' ? m.username.trim() : ''
  if (u && !isNonHumanB2bMemberUsername(u, m.userId)) {
    return { primary: u, secondary: null }
  }
  return { primary: 'Member', secondary: null }
}

/** Signed-in user slice used when the org members API row is missing profile fields (common for the current user). */
export interface B2bMemberViewerIdentity {
  id: string
  firstName: string | null
  lastName: string | null
  username: string | null
  email: string | null
}

/** Shown when a table cell has no value (align with joined-date column). */
export const B2B_MEMBER_TABLE_EMPTY_CELL = '—'

/**
 * Fills null/technical fields on the signed-in user's row from the session (DB can lag behind Supabase).
 */
export function mergeB2bMemberRowWithViewerIdentity(
  m: B2bMemberRow,
  viewer: B2bMemberViewerIdentity | null,
): B2bMemberRow {
  if (!viewer || m.userId !== viewer.id) return m
  const viewerEmailOk = displayableMemberEmail(viewer.email)

  const mu = typeof m.username === 'string' ? m.username.trim() : ''
  let mergedUsername = m.username
  if (!mu || isNonHumanB2bMemberUsername(mu, m.userId)) {
    const vu = typeof viewer.username === 'string' ? viewer.username.trim() : ''
    if (vu && !isNonHumanB2bMemberUsername(vu, m.userId)) {
      mergedUsername = viewer.username ?? ''
    }
  }

  return {
    ...m,
    firstName: m.firstName ?? viewer.firstName,
    lastName: m.lastName ?? viewer.lastName,
    email: displayableMemberEmail(m.email) ? m.email : viewerEmailOk ? viewer.email : m.email,
    username: mergedUsername,
  }
}

/** Single primary + optional @username; org directory uses `getB2bMemberTableNameAndEmail` instead. */
export function getB2bMemberNameCellsWithViewerFallback(
  m: B2bMemberRow,
  viewer: B2bMemberViewerIdentity | null,
): B2bMemberNameCells {
  const direct = getB2bMemberNameCells(m)
  if (!viewer || m.userId !== viewer.id || direct.primary !== 'Member') return direct
  return getB2bMemberNameCells(mergeB2bMemberRowWithViewerIdentity(m, viewer))
}

export interface B2bMemberTableNameEmail {
  nameColumn: string
  emailColumn: string
}

/**
 * Org admin member directory: **Name** and **Email** columns (no @handle line; handle is not shown here).
 */
export function getB2bMemberTableNameAndEmail(
  m: B2bMemberRow,
  viewer: B2bMemberViewerIdentity | null,
): B2bMemberTableNameEmail {
  const row = mergeB2bMemberRowWithViewerIdentity(m, viewer)
  const nameParts = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
  const email = displayableMemberEmail(row.email)

  if (nameParts) {
    return {
      nameColumn: nameParts,
      emailColumn: email || B2B_MEMBER_TABLE_EMPTY_CELL,
    }
  }
  if (email) {
    return { nameColumn: B2B_MEMBER_TABLE_EMPTY_CELL, emailColumn: email }
  }
  return { nameColumn: 'Member', emailColumn: B2B_MEMBER_TABLE_EMPTY_CELL }
}

export function getB2bMemberAdminTableSortKey(
  m: B2bMemberRow,
  viewer: B2bMemberViewerIdentity | null,
): string {
  const { nameColumn, emailColumn } = getB2bMemberTableNameAndEmail(m, viewer)
  if (nameColumn !== B2B_MEMBER_TABLE_EMPTY_CELL && nameColumn !== 'Member') {
    return nameColumn.toLowerCase()
  }
  if (emailColumn !== B2B_MEMBER_TABLE_EMPTY_CELL) return emailColumn.toLowerCase()
  return nameColumn.toLowerCase()
}

function sortKeyForMemberRow(m: B2bMemberRow, viewer: B2bMemberViewerIdentity | null): string {
  return getB2bMemberAdminTableSortKey(m, viewer)
}

/** Admins first, then alphabetical by display primary name. */
export function sortB2bMembersForAdminTable(
  members: B2bMemberRow[],
  viewer: B2bMemberViewerIdentity | null = null,
): B2bMemberRow[] {
  return [...members].sort((a, b) => {
    if (a.role !== b.role) {
      return a.role === 'ADMIN' ? -1 : 1
    }
    const ak = sortKeyForMemberRow(a, viewer)
    const bk = sortKeyForMemberRow(b, viewer)
    return ak.localeCompare(bk)
  })
}

/** Short local date for joined column; em dash when missing or invalid. */
export function formatB2bMemberJoinedLabel(iso: string): string {
  if (!iso?.trim()) return '—'
  const s = formatDateShort(iso)
  return s || '—'
}

/** Mirrors server `getB2bSelfLeaveBlockedReason` for UX (disable leave when sole admin). */
export interface B2bSelfLeaveUiState {
  canLeave: boolean
}

/**
 * @param isAdmin - Current user's membership role is ADMIN
 * @param members - Org member rows (admins only; empty for members — callers pass [] and set isAdmin false)
 */
export function getB2bSelfLeaveUiState(isAdmin: boolean, members: B2bMemberRow[]): B2bSelfLeaveUiState {
  if (!isAdmin) {
    return { canLeave: true }
  }
  const adminCount = members.filter((m) => m.role === 'ADMIN').length
  if (adminCount <= 1) {
    return { canLeave: false }
  }
  return { canLeave: true }
}

/**
 * Admin surface URL with join prefill (used after public `/join` redirect and for returnUrl).
 * Example: `/account-admin?join=ABCDEFGH`
 */
export function buildB2bOrgAccountAdminJoinPath(joinCodeRaw: string): string {
  const code = sanitizeJoinCodeInput(joinCodeRaw)
  return `/account-admin?join=${encodeURIComponent(code)}`
}

/**
 * Public member-facing invite path (short link on posters). Redirects to account-admin with join prefill.
 * Example: `/join?join=ABCDEFGH`
 */
export function buildB2bOrgInvitePathAndQuery(joinCodeRaw: string): string {
  const code = sanitizeJoinCodeInput(joinCodeRaw)
  return `/join?join=${encodeURIComponent(code)}`
}

/**
 * Absolute invite URL for sharing. Uses `window.location.origin` on web; otherwise `PRODUCTION_APP_ORIGIN`.
 */
export function buildB2bOrgInviteLink(joinCodeRaw: string): string {
  const pathAndQuery = buildB2bOrgInvitePathAndQuery(joinCodeRaw)
  const base =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : PRODUCTION_APP_ORIGIN.replace(/\/$/, '')
  return `${base}${pathAndQuery}`
}

/**
 * Public squad invite URL — recipients land on the branded `/wc-invite/<code>`
 * waiting room (`app/wc-invite/[code].tsx`) where they see "Presented by …"
 * before signing up. Signed-in invitees tap "I already have an account" and
 * `/sign-in` routes them straight through to auto-join.
 */
export function buildWorldCupGroupJoinAppUrl(inviteCodeRaw: string): string {
  const code = inviteCodeRaw.trim()
  if (!code) return ''
  // Delegates to the shared builder with the b2b origin style (trailing slash
  // stripped, no Platform.OS guard) so the emitted URL is byte-identical.
  return buildCompetitionSquadJoinUrl(WORLD_CUP_COMPETITION_ID, code, 'b2b')
}

/**
 * Primary link for **organization** membership (`/join?join=…` → account-admin join).
 * When a World Cup squad is linked, `POST /accounts/join` still adds them to the squad. Friends who
 * should join the squad only (no org) use squad invites from World Cup → Squads, not this URL.
 */
export function buildOrgMemberPrimaryInviteLink(options: {
  /** Kept for call-site compatibility; primary invite is always the org link. */
  worldCupSquadInviteCode?: string | null
  orgJoinCode: string
}): string {
  return buildB2bOrgInviteLink(options.orgJoinCode)
}

/**
 * Format a raw join code for display.
 * - 6 chars (current org default): `ABC-DEF` (aligned with short squad-style codes).
 * - Longer legacy codes: groups of four, e.g. 8 → `ABCD-EFGH`, 12 → `ABCD-EFGH-IJKL`.
 */
export function formatJoinCode(code: string): string {
  const clean = code.replace(/[^A-Z0-9]/gi, '').toUpperCase()
  if (clean.length <= 4) return clean
  if (clean.length === 6) {
    return `${clean.slice(0, 3)}-${clean.slice(3)}`
  }
  const parts: string[] = []
  for (let i = 0; i < clean.length; i += 4) {
    parts.push(clean.slice(i, i + 4))
  }
  return parts.join('-')
}

/**
 * Sanitize user input for a join code: strip dashes, trim, uppercase.
 */
export function sanitizeJoinCodeInput(input: string): string {
  return input.replace(/-/g, '').trim().toUpperCase()
}

/**
 * True when an invite URL `join` query is for the same organization as `accountJoinCode`
 * (dash / case insensitive).
 */
export function isB2bInviteJoinCodeForAccount(
  joinCodeFromInviteUrl: string | undefined,
  accountJoinCode: string | null | undefined,
): boolean {
  if (!joinCodeFromInviteUrl?.trim() || !accountJoinCode?.trim()) return false
  return sanitizeJoinCodeInput(joinCodeFromInviteUrl) === sanitizeJoinCodeInput(accountJoinCode)
}

/** Matches server `isValidHexColor` in `server/src/helpers/b2bAccount.helper.js`. */
export function isValidHexColor(hex: string | null | undefined): boolean {
  if (!hex || typeof hex !== 'string') return false
  return /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(hex.trim())
}
